import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { transitionHeight } from 'animations-lib';
import { SectionFileDatasourceService } from 'file-datasource-service-lib';
import { ISection } from 'interfaces-lib';
import { Observable, of } from 'rxjs';
import { map } from 'rxjs/operators';
import { DependenciesModelBuilderService } from '../../../services/dependencies-model-builder.service';
import { SectionComponentBase } from '../section-component-base';

class HtmlConfig {
  ui: { dependencies: string[] };

  constructor(ui: { dependencies: string[] }) {
    this.ui = ui;
  }

  static clone(config: HtmlConfig): HtmlConfig {
    return new HtmlConfig({
      dependencies: config.ui ? [...config.ui.dependencies] : [],
    });
  }
}

@Component({
    selector: 'app-html-docx-template-section',
    templateUrl: './html-docx-template-section.component.html',
    styleUrls: ['../docx-template-section/docx-template-section.component.scss'],
    animations: [transitionHeight],
    standalone: false
})
export class HtmlDocxTemplateSectionComponent extends SectionComponentBase implements OnInit, OnDestroy {
  @Input() formGroup: UntypedFormGroup;
  @Input() workflowConfigId: string;
  @Input() templateValid = false;

  showDependencies = false;

  constructor(private sectionService: SectionFileDatasourceService,
              private dependenciesModelBuilderService: DependenciesModelBuilderService
  ) {
    super();
  }

  public static override buildForm(formGroup: UntypedFormGroup, section?: ISection): void {
    formGroup.get('sourceType').setValue('S3');
    formGroup.addControl('file', new UntypedFormControl());
    formGroup.get('referenceId').setValue(section?.referenceId ?? 'HTML_DOCX_TEMPLATE_1');

    const config = section?.config ? HtmlConfig.clone(section.config as HtmlConfig) : new HtmlConfig({ dependencies: [] });
    formGroup.get('config').setValue(config);

    formGroup.get('model').setValue(section?.model ?? {});

    formGroup.get('required').setValue(formGroup.get('sourceValue').value !== null);
    formGroup.get('sourceValue').valueChanges.subscribe((filename) => formGroup.get('required').setValue(filename !== null));
  }

  ngOnInit(): void {
    this.enableDependencyToggle();
  }

  private enableDependencyToggle() {
    const sourceValueControl = this.formGroup.get('sourceValue');
    if (sourceValueControl?.value) {
      this.showDependencies = true;
    }
    sourceValueControl?.valueChanges.subscribe((value) => (this.showDependencies = true));
  }

  getInfo(): Observable<string> {
    let obs$: Observable<Blob>;
    const file = this.formGroup.get('file').value;
    if (file instanceof File) {
      obs$ = of(file);
    } else {
      obs$ = this.sectionService.downloadSectionFile(
        this.workflowConfigId,
        this.formGroup.get('id').value,
        this.formGroup.get('sourceValue').value
      );
    }

    return obs$.pipe(
      map((info) => {
        return `<p>Size: ${this.byteSizeToString(info.size)}</p>`;
      })
    );
  }

  getDependencies(): string[] {
    const htmlConfig = this.formGroup.get('config').value as HtmlConfig;
    return htmlConfig.ui.dependencies;
  }

  addDependency(dependencyPath: string): void {
    let model = this.formGroup.get('model').value;
    model = this.dependenciesModelBuilderService.insertInto(model, dependencyPath);
    this.formGroup.get('model').setValue(model);
    this.formGroup.get('model').markAsDirty();
  }

  removeDependency(removedPath: string): void {
    // brute force rebuild of the model
    let model = {};
    this.getDependencies().forEach((dependencyPath) => {
      model = this.dependenciesModelBuilderService.insertInto(model, dependencyPath);
    });
    this.formGroup.get('model').setValue(model);
    this.formGroup.get('model').markAsDirty();
  }

  ngOnDestroy(): void {
    console.log('Destroying HtmlDocxTemplateSectionComponent');
  }

  private byteSizeToString(byteCount: number): string {
    const sizes = ['Bytes', 'KB', 'MB'];
    const index = Math.floor(Math.log(byteCount) / Math.log(1024));
    const result = parseFloat((byteCount / Math.pow(1024, index)).toFixed(1));
    return `${result} ${sizes[index]}`;
  }
}
